/* eslint-disable max-len */
import { api } from './api';
import { store } from '../app/store';
import { setTenant } from '../features/tenant/tenantSlice';
import {
  ThemePreset,
  applyThemePreset,
  normalizeThemePreset,
} from './themePreset';
import { setAnalyticsBrand } from './analytics';
import { isAperioHost } from './aperioTracking';
import {
  PresetBrand,
  bookingTitle,
  presetAbsoluteUrl,
  presetBrand,
} from './presetBrand';

export type TenantConfig = {
  id: number;
  name: string;
  siteTitle: string;
  logoUrl: string;
  faviconUrl: string;
  image: string;
  /**
   * Public marketing site the visitor came from, e.g.
   * `https://aperiomobility.com`. Null for a tenant that has none, which is
   * every tenant today except Aperio.
   */
  marketingSiteUrl?: string | null;
  /** `default` (or null/absent) keeps the portal exactly as it is today. */
  themePreset?: string | null;
  /**
   * True only for tenants that store abandoned booking drafts (Tenant
   * Settings -> Capture drafts). Absent / false: the schedule step never
   * posts a draft.
   */
  captureDrafts?: boolean;
  /** The tenant's subdomain, e.g. `aperio` (matches a referral code's tenant). */
  subdomain?: string;
  /**
   * Cents a referred friend gets off their first plan booking (Tenant
   * Settings). 0 / absent - every tenant by default - means nothing.
   */
  referralFriendRewardCents?: number;
  colors: {
    primaryLightest: string;
    primaryLighter: string;
    primary: string;
    primaryDarker: string;
    primaryDarkest: string;
    logoColor: string;
    navBarTextColor: string;
    buttonColor: string;
    bookingStepsTextColor: string;
    bookingStepsTextAccentColor: string;
  };
};

// eslint-disable-next-line max-len, no-nested-ternary
const withHash = (v?: string) => (v ? (v.startsWith('#') ? v : `#${v}`) : undefined);

function setLink(rel: string, href: string, type?: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel~='${rel}']`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
  if (type) link.type = type;
}

function setMeta(key: string, content: string) {
  const isOG = key.startsWith('og:');
  const isTwitter = key.startsWith('twitter:');

  // Spec-preferred attribute
  const attr = isOG ? 'property' : 'name';
  const selector = `meta[${attr}="${key}"]`;

  // Update existing correct-attribute tags (all of them)
  let metas = Array.from(document.head.querySelectorAll<HTMLMetaElement>(selector));
  if (metas.length === 0) {
    const el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    metas = [el];
  }
  metas.forEach((m) => m.setAttribute('content', content));

  // Clean up duplicates using the *other* attribute, e.g. name="og:title" or property="twitter:title"
  const otherAttr = attr === 'name' ? 'property' : 'name';
  const wrongOnes = Array.from(document.head.querySelectorAll<HTMLMetaElement>(`meta[${otherAttr}="${key}"]`));
  wrongOnes.forEach((el) => el.remove());
}

const API_BASE = (api as any)?.defaults?.baseURL?.replace(/\/$/, '') ?? '';

function absolute(u?: string) {
  if (!u) return u;
  return /^https?:\/\//i.test(u) ? u
    : `${API_BASE}${u.startsWith('/') ? u : `/${u}`}`;
}

/**
 * Everything in the document head that names the brand, for a tenant on a
 * theme preset: title, description, Open Graph / Twitter tags, theme-color,
 * favicon, apple-touch-icon and the web-app manifest. public/index.html has
 * already done the same synchronously on the preset's own host (so nothing -
 * GTM included - reads a Go Oil value first); this covers every other host the
 * tenant is served on and keeps the two in step.
 */
export function applyPresetHead(brand: PresetBrand): void {
  const title = bookingTitle(brand.name);
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  document.title = title;
  setMeta('description', brand.description);
  setMeta('og:title', title);
  setMeta('og:description', brand.description);
  setMeta('og:site_name', brand.name);
  setMeta('og:type', 'website');
  setMeta('og:image', presetAbsoluteUrl(brand.shareImage));
  setMeta('og:image:alt', brand.shareImageAlt);
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', brand.description);
  setMeta('twitter:image', presetAbsoluteUrl(brand.shareImage));
  setMeta('theme-color', brand.themeColor);
  setLink('icon', `${base}/${brand.icon}`, brand.iconType);
  setLink('apple-touch-icon', `${base}/${brand.appleTouchIcon}`);
  setLink('manifest', `${base}/${brand.manifest}`);
}

async function applyTenantConfig(
  cfg: TenantConfig,
): Promise<TenantConfig | undefined> {
  try {
    const root = document.documentElement;

    const c = cfg.colors || {};
    const set = (k: string, v?: string) => v && root.style.setProperty(k, v);

    // A preset is a whole palette + type system, so it owns the colour
    // variables; the per-tenant colour columns are only written for the
    // default preset, which is every tenant that has no themePreset set.
    // Awaited so the preset's stylesheets are in before the app renders.
    const preset: ThemePreset = normalizeThemePreset(cfg.themePreset);
    if (preset !== 'default') {
      await applyThemePreset(preset);
    } else {
      set('--color-primary-lightest', withHash(c.primaryLightest));
      set('--color-primary-lighter', withHash(c.primaryLighter));
      set('--color-primary', withHash(c.primary));
      set('--color-primary-darker', withHash(c.primaryDarker));
      set('--color-primary-darkest', withHash(c.primaryDarkest));
      set('--color-logo', withHash(c.logoColor));
      set('--color-button', withHash(c.buttonColor));
      set('--color-navbar-text', withHash(c.navBarTextColor));
      set('--color-booking-steps-text', withHash(c.bookingStepsTextColor));
      set('--color-booking-steps-accent-text',
        withHash(c.bookingStepsTextAccentColor));
    }

    // A preset brings its own identity - name, description, icons, manifest,
    // share image, browser-chrome colour - the way it brings its own palette,
    // so none of the per-tenant columns (which may hold Go Oil values, e.g. a
    // siteTitle or favicon copied from the Go Oil row) can reach the head.
    // Every tenant on the default preset takes the branch below, unchanged.
    const brand = presetBrand(cfg.themePreset);
    if (brand) {
      applyPresetHead(brand);
    } else {
      const title = cfg.siteTitle || cfg.name;
      if (title) {
        document.title = `Book Online | ${title}`;
        setMeta('og:title', title);
        setMeta('twitter:title', title);
      }

      const desc = `${title} is car maintenance made easy. Our service vans come to you and change your car's oil, rotate your truck's tires, replace your SUV's battery and more`
        || 'Go Oil is car maintenance made easy. Our service vans come to you and change your car\'s oil, rotate your truck\'s tires, replace your SUV\'s battery and more';
      setMeta('description', desc);
      setMeta('og:description', desc);
      setMeta('twitter:description', desc);

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (c.primary) setMeta('theme-color', withHash(c.primary)!);

      if (cfg.faviconUrl) {
        const href = absolute(cfg.faviconUrl);
        if (href) {
          setLink('icon', href);
          setLink('apple-touch-icon', href);
          setMeta('og:image', href!);
          setMeta('twitter:image', href!);
          setMeta('twitter:card', 'summary_large_image');
        }
      }
    }
    // The tenant's `image` column is the booking page's decorative art, and
    // an inline style beats every rule in a preset's stylesheet. A preset
    // brings its own art - the Aperio one is the marketing site's live scene
    // (components/AperioLiveScene.tsx) plus theme.css's mascot for the other
    // pages - so the column is only applied where nothing else owns the slot,
    // which is every tenant on the default preset.
    if (cfg.image && preset === 'default') {
      const href = absolute(cfg.image);
      document.documentElement.style.setProperty('--car-image', `url(${href})`);
    }

    // The marketing site and this portal report into ONE Mixpanel project so a
    // visitor's funnel joins end to end; `brand` is what lets a board filter
    // back down to a single brand. Set here because this is where the
    // tenant identity first becomes known: 'aperio' for the Aperio tenant
    // (preset / subdomain), 'gooil' for every other one - never the raw
    // tenant name, which is how 'aperiomobility' reached the boards.
    setAnalyticsBrand(cfg);

    store.dispatch(setTenant({
      id: cfg.id,
      name: cfg.name,
      siteTitle: cfg.siteTitle,
      logoUrl: cfg.logoUrl,
      faviconUrl: cfg.faviconUrl,
      image: cfg.image,
      colors: {
        primaryLightest: '',
        primaryLighter: '',
        primary: '',
        primaryDarker: '',
        primaryDarkest: '',
        logoColor: '',
        buttonColor: '',
        navBarTextColor: '',
        bookingStepsTextColor: '',
        bookingStepsTextAccentColor: '',
      },
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-underscore-dangle
    (window as any).__TENANT__ = cfg;

    return cfg;
  } catch (e) {
    return undefined;
  }
}

/** How long to wait before asking for the tenant a second time. */
export const TENANT_RETRY_MS = 600;

async function fetchTenantConfig(): Promise<TenantConfig | undefined> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await api.get<TenantConfig>('api/tenant');
      if (data && typeof data === 'object') return data;
    } catch (e) {
      // retried once below
    }
    if (attempt === 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, TENANT_RETRY_MS); });
    }
  }
  return undefined;
}

/**
 * The tenant a page host names on its own, for when GET api/tenant cannot
 * be read at all (QA 2026-09-25 #15: one run in six on
 * book.aperiomobility.com hit a CORS failure and rendered as Go Oil). Only
 * Aperio's hosts have one; every other host keeps today's behaviour.
 */
export function hostFallbackTenant(
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): TenantConfig | undefined {
  if (!isAperioHost(host)) return undefined;
  return {
    id: 0,
    name: 'Aperio',
    siteTitle: 'Aperio',
    logoUrl: '',
    faviconUrl: '',
    image: '',
    marketingSiteUrl: 'https://aperiomobility.com',
    themePreset: 'aperio',
    subdomain: 'aperio',
    // Features that need the tenant's own settings stay off until it loads.
    captureDrafts: false,
    referralFriendRewardCents: 0,
    colors: {
      primaryLightest: '',
      primaryLighter: '',
      primary: '',
      primaryDarker: '',
      primaryDarkest: '',
      logoColor: '',
      navBarTextColor: '',
      buttonColor: '',
      bookingStepsTextColor: '',
      bookingStepsTextAccentColor: '',
    },
  };
}

export async function bootTheme(): Promise<TenantConfig | undefined> {
  const cfg = (await fetchTenantConfig()) ?? hostFallbackTenant();
  return cfg ? applyTenantConfig(cfg) : undefined;
}
